import { useEffect, useRef } from "react";
import {
  Button,
  MessageBar,
  MessageBarBody,
  Popover,
  PopoverSurface,
  PopoverTrigger,
  Spinner,
  Text,
  makeStyles,
  shorthands,
  tokens,
} from "@fluentui/react-components";
import { useConversation } from "./useConversation";
import { MessageBubble } from "./components/MessageBubble";
import { QuestionInput } from "./components/QuestionInput";
import { RecapPanel } from "./components/RecapPanel";
import { ValidationGate } from "./components/ValidationGate";
import { SuccessScreen } from "./components/SuccessScreen";

const HELP_TEXT = `Décrivez votre besoin au fil de la conversation. L'assistant vous pose les questions
nécessaires (type d'opération, dates, livrables, visuels…) et remplit le brief au fur et à mesure,
visible dans le panneau de droite. Quand tout est collecté, validez pour générer le document.`;

const useStyles = makeStyles({
  layout: {
    display: "grid",
    gridTemplateColumns: "minmax(0, 3fr) minmax(280px, 1fr)",
    columnGap: "16px",
    alignItems: "start",
    "@media (max-width: 900px)": { gridTemplateColumns: "1fr" },
  },
  conversation: { display: "flex", flexDirection: "column", rowGap: "12px", minWidth: 0 },
  header: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    columnGap: "8px",
  },
  headerActions: { display: "flex", gap: "8px" },
  messages: {
    display: "flex",
    flexDirection: "column",
    ...shorthands.padding("16px"),
    backgroundColor: tokens.colorNeutralBackground1,
    ...shorthands.borderRadius(tokens.borderRadiusLarge),
    ...shorthands.border("1px", "solid", tokens.colorNeutralStroke2),
    minHeight: "240px",
    maxHeight: "60vh",
    overflowY: "auto",
  },
  help: { maxWidth: "320px", lineHeight: tokens.lineHeightBase300 },
});

export function ConversationView() {
  const styles = useStyles();
  const conv = useConversation();
  const bottomRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    bottomRef.current?.scrollIntoView({ behavior: "smooth", block: "end" });
  }, [conv.messages]);

  if (conv.generationSuccess) {
    return <SuccessScreen documentUrl={conv.documentUrl} onNewBrief={conv.reset} />;
  }

  const busy = conv.phase === "sending" || conv.phase === "generating";

  return (
    <div className={styles.layout}>
      <section className={styles.conversation}>
        <div className={styles.header}>
          <Text as="h1" size={600} weight="semibold">
            Nouveau brief
          </Text>
          <div className={styles.headerActions}>
            <Popover>
              <PopoverTrigger disableButtonEnhancement>
                <Button appearance="subtle">💡 Comment ça marche</Button>
              </PopoverTrigger>
              <PopoverSurface>
                <Text className={styles.help}>{HELP_TEXT}</Text>
              </PopoverSurface>
            </Popover>
            <Button appearance="secondary" onClick={conv.reset}>
              ＋ Nouveau
            </Button>
          </div>
        </div>

        <div className={styles.messages}>
          {conv.messages.map((m, i) => (
            <MessageBubble key={i} message={m} />
          ))}
          {conv.phase === "initializing" && <Spinner label="Initialisation..." />}
          {busy && <Spinner size="tiny" label="En cours..." />}
          <div ref={bottomRef} />
        </div>

        {conv.error && (
          <MessageBar intent="error">
            <MessageBarBody>Erreur : {conv.error}</MessageBarBody>
          </MessageBar>
        )}

        {conv.phase !== "initializing" &&
          (conv.waitingForValidation ? (
            <ValidationGate
              onValidate={() => void conv.validateAndGenerate()}
              onModify={conv.modify}
              disabled={busy}
            />
          ) : (
            <QuestionInput conv={conv} disabled={busy} />
          ))}
      </section>

      <RecapPanel briefData={conv.briefData} stagedFiles={conv.stagedFiles} />
    </div>
  );
}
